import { Link, useForm } from '@inertiajs/react'
import AdminLayout from '../../../components/AdminLayout'
import { OrderStatus } from '../../../components/admin/OrderStatus'
import { fontLabel as stampFontLabel } from '@/components/StampStep'

interface OrderItem {
  id: number
  productId: number
  productName: string
  quantity: number
  price: number
  subtotal: number
  // Whatever the configurator asked at purchase time, in order.
  // Engraving lines also carry the hand, size and ink the customer
  // picked — without them the workshop can't reproduce the stamp.
  customizations: {
    label: string
    value: string | null
    textFont: string | null
    textSize: number | null
    textColor: string | null
  }[]
}

interface Order {
  id: number
  status: string
  statusLabel: string
  customerName: string
  customerEmail: string
  customerPhone: string | null
  shippingAddress: string
  billingAddress: string | null
  cardBrand: string | null
  cardLast4: string | null
  total: number
  createdAt: string
  items: OrderItem[]
}

interface Props {
  order: Order
  statuses: string[]
}

export default function AdminOrdersShow({ order, statuses }: Props) {
  const { data, setData, patch, processing } = useForm({ status: order.status })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/admin/orders/${order.id}`)
  }

  const orderNumber = `SSS-${String(order.id).padStart(4, '0')}`

  return (
    <AdminLayout>
      <Link href="/admin/orders" className="sss-admin-back">
        ← Back to orders
      </Link>

      <div className="sss-admin-head">
        <div className="sss-admin-head__row">
          <div>
            <p className="sss-admin-eyebrow sss-admin-eyebrow--accent">Order {orderNumber}</p>
            <h1 className="sss-admin-title">Order #{order.id}</h1>
            <p className="sss-admin-sub">Placed on {order.createdAt}</p>
          </div>
          <OrderStatus status={order.status} label={order.statusLabel} />
        </div>
      </div>

      <div className="sss-admin-cols">
        <div>
          {/* Order Items */}
          <div className="sss-admin-card">
            <div className="sss-admin-card__head">
              <span className="sss-admin-card__title">Order Items</span>
            </div>
            <div className="sss-admin-tablewrap">
              <table className="sss-admin-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Price</th>
                    <th>Qty</th>
                    <th className="sss-admin-table__num">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Link
                          href={`/admin/products/${item.productId}/edit`}
                          className="sss-admin-action"
                        >
                          {item.productName}
                        </Link>
                        {item.customizations.length > 0 && (
                          <div className="sss-admin-table__muted" style={{ marginTop: 4, fontSize: 12 }}>
                            {item.customizations
                              .filter((entry) => entry.value)
                              .map((entry) => (
                                <div key={entry.label}>
                                  {entry.label}: {entry.value}
                                  {entry.textFont && (
                                    <> · {stampFontLabel(entry.textFont)}</>
                                  )}
                                  {entry.textSize && <> · {entry.textSize}px</>}
                                  {entry.textColor && (
                                    <>
                                      {' · '}
                                      <span
                                        aria-hidden="true"
                                        style={{
                                          display: 'inline-block',
                                          width: 10,
                                          height: 10,
                                          borderRadius: 999,
                                          background: entry.textColor,
                                          border: '1px solid var(--sss-hairline)',
                                          verticalAlign: 'middle',
                                          marginRight: 4,
                                        }}
                                      />
                                      {entry.textColor}
                                    </>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </td>
                      <td className="sss-admin-table__muted">${item.price.toFixed(2)}</td>
                      <td className="sss-admin-table__muted">{item.quantity}</td>
                      <td className="sss-admin-table__num sss-admin-table__strong">
                        ${item.subtotal.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  <tr className="sss-admin-table__foot">
                    <td colSpan={3} className="sss-admin-table__num">
                      Total
                    </td>
                    <td className="sss-admin-table__num">${order.total.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Customer */}
          <div className="sss-admin-card">
            <div className="sss-admin-card__head">
              <span className="sss-admin-card__title">Customer</span>
            </div>
            <div className="sss-admin-card__body">
              <div className="sss-admin-rows">
                <Row label="Name" value={order.customerName} />
                <Row label="Email" value={order.customerEmail} />
                {order.customerPhone && <Row label="Phone" value={order.customerPhone} />}
                {order.cardLast4 && (
                  <Row label="Card" value={`${order.cardBrand ?? 'Card'} •••• ${order.cardLast4}`} />
                )}
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="sss-admin-card">
            <div className="sss-admin-card__head">
              <span className="sss-admin-card__title">Shipping</span>
            </div>
            <div className="sss-admin-card__body">
              <p className="sss-body" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {order.shippingAddress}
              </p>
              {order.billingAddress && (
                <>
                  <div className="sss-admin-label" style={{ margin: '20px 0 8px' }}>
                    Billing
                  </div>
                  <p className="sss-body" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {order.billingAddress}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Status update */}
        <div className="sss-admin-sticky">
          <div className="sss-admin-card">
            <div className="sss-admin-card__head">
              <span className="sss-admin-card__title">Update Status</span>
            </div>
            <div className="sss-admin-card__body">
              <form onSubmit={handleSubmit} className="sss-admin-form">
                <div className="sss-admin-field">
                  <label htmlFor="status" className="sss-admin-label">
                    Order status
                  </label>
                  <select
                    id="status"
                    className="sss-admin-select"
                    value={data.status}
                    onChange={(e) => setData('status', e.target.value)}
                  >
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="sss-admin-btn sss-admin-btn--block"
                  disabled={processing || data.status === order.status}
                >
                  {processing ? 'Updating…' : 'Update status'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="sss-admin-row">
      <span className="sss-admin-row__k">{label}</span>
      <span className="sss-admin-row__v">{value}</span>
    </div>
  )
}
